// Where the ground floor's documents lie in the art, and how the camera leans over them. The art records each object
// it draws for the UI (cast.anchor in frontend/art/r_ground_work.py); the screens place their real DOM over those
// rects with the camera variables WorldStage sets on :root (--camx, --camy, --wp). The fallbacks are the same rects as
// built, for the moment before world.json arrives.
import { useEffect, useState } from 'react';
import { meta } from '../../world/assets';
import { fitScale, type Viewport } from '../../world/camera';

export type Box = [number, number, number, number];

const FALLBACK: Record<string, Box> = {
  'clerk.docket': [1312, 155, 160, 74],
  'clerk.roll': [1220, 155, 84, 74],
  'clerk.stamp': [1478, 172, 40, 34],
  'police.officer': [1436, 743, 14, 14],
  'police.slip': [1448, 777, 18, 7],
};

/** A room's x range in map pixels, walls included (to keep a leaning camera inside it). */
export function roomSpan(room: string, fallback: [number, number]): [number, number] {
  const r = meta()?.rooms?.[room]?.rect;
  return r ? [r[0] - 6, r[0] + r[2] + 6] : fallback;
}

/** An object's rect in map pixels: [x, y, w, h]. */
export function anchorBox(name: string): Box {
  const a = meta()?.anchors?.[name];
  return a ? [a[0], a[1], a[2], a[3]] : FALLBACK[name];
}

function viewportNow(): Viewport {
  if (typeof window === 'undefined') return { w: 1280, h: 800, dpr: 1 };
  return { w: window.innerWidth, h: window.innerHeight, dpr: Math.max(1, Math.min(3, window.devicePixelRatio || 1)) };
}

/** The viewport, re-read on resize (the framing of a document depends on it). */
export function useViewport(): Viewport {
  const [vp, setVp] = useState<Viewport>(viewportNow);
  useEffect(() => {
    const on = () => setVp(viewportNow());
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return vp;
}

export interface Lean {
  /** For the scene: the window the camera frames (fixes the zoom) and where it centres. */
  focus: [number, number];
  center: [number, number];
  /** CSS px per art px at that zoom, and the centre: the CSS fallbacks before the stage has drawn. */
  wp: number;
}

/**
 * Lean over an object lying in a room. `focus` sets the zoom (the stage picks the largest whole scale that fits it).
 * Horizontally the camera centres on `span` (art x range to keep in view, default the object). Vertically, either the
 * object's bottom edge sits `bottomCss` above the bottom of the screen (a desk you look down at), or its top edge sits
 * at `topFrac` of the screen height (a phone, where the paper runs on down the screen).
 */
export function lean(box: Box, focus: [number, number], vp: Viewport,
  opts: { bottomCss?: number; topFrac?: number; span?: [number, number]; within?: [number, number] }): Lean {
  const scale = fitScale(focus[0], focus[1], vp);
  const rows = (vp.h * vp.dpr) / scale;
  const cols = (vp.w * vp.dpr) / scale;
  const [x0, x1] = opts.span ?? [box[0], box[0] + box[2]];
  let cx = (x0 + x1) / 2;
  // keep the view inside the room's walls where it can (no empty corridor beside an object at the room's edge)
  if (opts.within && opts.within[1] - opts.within[0] > cols) {
    cx = Math.min(Math.max(cx, opts.within[0] + cols / 2), opts.within[1] - cols / 2);
  }
  let cy: number;
  if (opts.topFrac !== undefined) cy = box[1] + (0.5 - opts.topFrac) * rows;
  else cy = box[1] + box[3] + ((opts.bottomCss ?? 48) * vp.dpr) / scale - rows / 2;
  return { focus, center: [Math.round(cx), Math.round(cy)], wp: scale / vp.dpr };
}

/** Inline CSS variables for an element glued to a box: --bx, --by, --bw, --bh, and the fallback camera. */
export function boxVars(box: Box, l: Lean, prefix = 'b'): Record<string, string> {
  return {
    [`--${prefix}x`]: String(box[0]), [`--${prefix}y`]: String(box[1]),
    [`--${prefix}w`]: String(box[2]), [`--${prefix}h`]: String(box[3]),
    '--fcx': String(l.center[0]), '--fcy': String(l.center[1]), '--fwp': `${l.wp}px`,
  };
}
